import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  MapPin, Users, DollarSign, TrendingUp, Download,
  Loader2, RefreshCw, Building2, Filter, ArrowRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import api from '../../../utils/api';
import { toast } from '../../../hooks/use-toast';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];

const DestinationReportsTab = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [reportData, setReportData] = useState(null);
  const [operatorData, setOperatorData] = useState(null);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReportData();
  }, [period]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [destRes, opRes] = await Promise.all([
        api.get(`/admin/bus/reports/destinations?period=${period}`),
        api.get(`/admin/bus/reports/destinations/by-operator?period=${period}`)
      ]);

      setReportData(destRes.data);
      setOperatorData(opRes.data);
    } catch (error) {
      console.error('Error fetching destination reports:', error);
      toast({ title: 'Error', description: 'Failed to load destination reports', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async (destination) => {
    try {
      const response = await api.get(`/admin/bus/reports/destinations/trends?destination=${destination}&period=${period}`);
      setTrendData(response.data);
      setSelectedDestination(destination);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load trend data', variant: 'destructive' });
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/bus/reports/destinations/export/csv?period=${period}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `destination_report_${period}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({ title: 'Success', description: 'CSV report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export CSV', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => `K${value?.toLocaleString() || 0}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-500">Loading destination reports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="destination-reports-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Destination Reports</h2>
          <p className="text-sm text-gray-500">Passenger distribution by destination along routes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['7d', '30d', '90d'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  period === p
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-1">CSV</span>
          </Button>

          <Button variant="outline" size="sm" onClick={fetchReportData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50">
            <div className="p-2 bg-indigo-100 rounded-lg w-fit">
              <MapPin className="h-5 w-5 text-indigo-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-3">{reportData.summary.totalDestinations}</p>
            <p className="text-sm text-gray-500">Total Destinations</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="p-2 bg-green-100 rounded-lg w-fit">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-3">{reportData.summary.totalPassengers}</p>
            <p className="text-sm text-gray-500">Total Passengers</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50">
            <div className="p-2 bg-blue-100 rounded-lg w-fit">
              <DollarSign className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-3">{formatCurrency(reportData.summary.totalRevenue)}</p>
            <p className="text-sm text-gray-500">Total Revenue</p>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-orange-50 to-amber-50">
            <div className="p-2 bg-orange-100 rounded-lg w-fit">
              <TrendingUp className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 mt-3">{reportData.summary.intermediateStopBookings}</p>
            <p className="text-sm text-gray-500">Intermediate Stops</p>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Destinations List */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-600" />
            Passengers by Destination
          </h3>

          {reportData?.destinations?.length > 0 ? (
            <div className="space-y-3">
              {reportData.destinations.map((dest, index) => (
                <div
                  key={dest.destination}
                  className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => fetchTrendData(dest.destination)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{dest.destination}</p>
                        <p className="text-xs text-gray-500">
                          {dest.isIntermediateStop ? '📍 Intermediate Stop' : '🏁 Final Destination'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600">{dest.passengerCount} passengers</p>
                      <p className="text-xs text-gray-500">{formatCurrency(dest.totalRevenue)}</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="relative pt-1">
                    <div className="overflow-hidden h-2 text-xs flex rounded bg-indigo-100">
                      <div
                        style={{ width: `${dest.passengerShare}%` }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500"
                        style={{ width: `${dest.passengerShare}%`, backgroundColor: COLORS[index % COLORS.length] }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">{dest.passengerShare}% of passengers</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No destination data available</p>
            </div>
          )}
        </Card>

        {/* Pie Chart */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">Passenger Distribution</h3>

          {reportData?.destinations?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={reportData.destinations}
                  dataKey="passengerCount"
                  nameKey="destination"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ destination, passengerShare }) => `${destination} (${passengerShare}%)`}
                >
                  {reportData.destinations.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} passengers`, 'Passengers']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No data to display
            </div>
          )}
        </Card>
      </div>

      {/* Trends for Selected Destination */}
      {selectedDestination && trendData && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              Trends for {selectedDestination}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedDestination(null)}>
              Clear
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-indigo-50 rounded-lg text-center">
              <p className="text-xl font-bold text-indigo-600">{trendData.summary.totalPassengers}</p>
              <p className="text-xs text-gray-600">Total Passengers</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xl font-bold text-green-600">{formatCurrency(trendData.summary.totalRevenue)}</p>
              <p className="text-xs text-gray-600">Total Revenue</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xl font-bold text-blue-600">{trendData.summary.avgDailyPassengers}</p>
              <p className="text-xs text-gray-600">Avg Daily</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg text-center">
              <p className="text-xl font-bold text-orange-600">{trendData.summary.daysWithBookings}</p>
              <p className="text-xs text-gray-600">Active Days</p>
            </div>
          </div>

          {trendData.dailyTrends?.length > 0 && (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trendData.dailyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  tick={{ fontSize: 11 }}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                  formatter={(value, name) => [value, name === 'passengers' ? 'Passengers' : formatCurrency(value)]}
                />
                <Bar dataKey="passengers" fill="#6366f1" name="Passengers" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {/* Operator Breakdown */}
      {operatorData?.operators?.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Destinations by Operator
          </h3>

          <div className="space-y-4">
            {operatorData.operators.map((op) => (
              <div key={op.operatorId} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold">{op.operatorName}</p>
                    <p className="text-sm text-gray-500">{op.destinations.length} destinations served</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-indigo-600">{op.totalPassengers} passengers</p>
                    <p className="text-sm text-gray-500">{formatCurrency(op.totalRevenue)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {op.destinations.map((dest, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-white rounded-full text-sm border flex items-center gap-1"
                    >
                      {dest.destination}
                      <span className="text-xs text-gray-500">({dest.passengers})</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default DestinationReportsTab;
